/**
 * planService.js
 *
 * The trainer. Turns three inputs into a week of sessions:
 *   tier        0 no gym, 1 hotel gym, 2 standard gym, 3 well equipped
 *   minutes     20 | 30 | 45 | 60 | 90
 *   daysPerWeek 1..6
 *
 * No Firebase, no React, no network. Pure functions over the static library,
 * which makes it trivially testable and lets it run with no signal.
 */

import { rankSwapCandidates, filterNearDuplicates } from './selectionService.js';

// ---------------------------------------------------------------- splits

/**
 * Split by days per week.
 *
 * 1 to 2 days: cover the whole body every session, there is no room to
 *              specialise.
 * 3 days:      push / pull / legs. Covers everything by grouping muscles that
 *              already work together. A body-part split at 3 days would leave
 *              whole areas untrained.
 * 4 to 6 days: target body areas directly. At this frequency there IS room to
 *              give an area its own session, which is what most people mean by
 *              a training split.
 */
export const SPLITS = {
  1: ["full-body"],
  2: ["upper", "lower"],
  3: ["push", "pull", "legs"],
  4: ["chest", "back", "legs", "shoulders"],
  5: ["chest", "back", "legs", "shoulders", "arms"],
  6: ["chest", "back", "legs", "shoulders", "arms", "legs"],
};

/**
 * With no equipment at all there is almost no back work available: every
 * vertical pull needs a bar, and rows need something to hold. A pull day at
 * tier 0 would be one exercise long. A real trainer would not program it, so
 * neither do we. Tier 0 gets full-body sessions instead.
 */
const TIER0_SPLITS = {
  1: ["full-body"],
  2: ["full-body", "lower"],
  3: ["full-body", "lower", "full-body"],
  4: ["full-body", "lower", "full-body", "lower"],
  5: ["full-body", "lower", "full-body", "lower", "full-body"],
  6: ["full-body", "lower", "full-body", "lower", "full-body", "lower"],
};

export function chooseSplit(daysPerWeek, tier) {
  const table = tier === 0 ? TIER0_SPLITS : SPLITS;
  const split = table[daysPerWeek];
  if (!split) throw new Error(`unsupported daysPerWeek: ${daysPerWeek}`);
  return split;
}

/**
 * Each focus is a priority-ordered list of movement patterns.
 * Ordering matters: the session fills from the top, so compounds that
 * deserve fresh energy get picked before arm isolation.
 */
const FOCUS_PATTERNS = {
  push: ["horizontal-push", "vertical-push", "isolation", "horizontal-push"],
  pull: ["vertical-pull", "horizontal-pull", "isolation", "horizontal-pull"],
  legs: ["squat", "hinge", "lunge", "isolation", "core"],
  glutes: ["hinge", "hinge", "isolation", "isolation"],
  upper: ["horizontal-push", "vertical-pull", "vertical-push", "horizontal-pull", "isolation"],
  lower: ["squat", "hinge", "lunge", "isolation", "core"],
  "full-body": ["squat", "horizontal-push", "horizontal-pull", "hinge", "vertical-push", "core"],
  // Body-area focuses, used at 4+ days a week.
  chest: ["horizontal-push", "horizontal-push", "isolation", "vertical-push"],
  back: ["vertical-pull", "horizontal-pull", "horizontal-pull", "isolation"],
  shoulders: ["vertical-push", "isolation", "core", "isolation"],
  arms: ["isolation", "isolation", "isolation", "isolation"],
};

/** Muscle groups each focus is allowed to draw from. */
const FOCUS_GROUPS = {
  push: ["chest", "shoulders", "triceps"],
  pull: ["back", "biceps"],
  legs: ["legs", "core"],
  glutes: ["legs", "core"],
  upper: ["chest", "back", "shoulders", "biceps", "triceps"],
  lower: ["legs", "core"],
  "full-body": ["chest", "back", "shoulders", "legs", "core", "biceps", "triceps"],
  // Chest day carries triceps and back day carries biceps, because pressing
  // and pulling already work them. That is why 4 days needs no separate arm day.
  chest: ["chest", "triceps"],
  back: ["back", "biceps"],
  shoulders: ["shoulders", "core"],
  arms: ["biceps", "triceps"],
};

// ------------------------------------------------------------ rationale

/**
 * Why this split. The app should never hand someone a plan without saying
 * what reasoning produced it, or they cannot tell a considered choice from a
 * random one.
 */
export function explainSplit(daysPerWeek, tier) {
  const split = chooseSplit(daysPerWeek, tier);
  const named = split.join(" / ");

  if (tier === 0) {
    return `With no equipment there is almost no back work available, because `
      + `every vertical pull needs a bar. A push/pull/legs week would hand you a `
      + `one-exercise pull day, so this is ${named} instead.`;
  }
  if (daysPerWeek <= 2) {
    return `At ${daysPerWeek} day${daysPerWeek > 1 ? "s" : ""} a week there is not `
      + `enough room to give muscles their own sessions, so ${named} covers your `
      + `whole body each time you train.`;
  }
  if (daysPerWeek === 3) {
    return `Three days gives you ${named}. This covers your whole body in three `
      + `sessions by grouping muscles that already work together. A chest / biceps / `
      + `legs split would fit the same three days but never train your back or `
      + `shoulders at all.`;
  }
  if (daysPerWeek === 4) {
    return `Four days is enough to give body areas their own sessions, so this is `
      + `${named}. Your triceps get worked on chest day and your biceps on back day, `
      + `which is why there is no separate arm day at this frequency.`;
  }
  return `${daysPerWeek} days gives you ${named}, one body area per session with `
    + `room for a dedicated arm day.`;
}

/** Why this exercise, in this slot, at this tier. */
export function explainChoice(exercise, slotIndex, tier) {
  const bits = [];

  if (slotIndex === 0) {
    bits.push("Hardest compound of the session, so it goes first while you are fresh");
  } else if (exercise.mechanic === "isolation") {
    bits.push(`Isolation work for ${exercise.primaryMuscles[0] || exercise.muscleGroup}, after the compounds`);
  } else {
    bits.push(`Second ${exercise.movementPattern.replace("-", " ")} angle, to add volume without repeating the first lift`);
  }

  if (exercise.minTier === tier && tier > 0) {
    bits.push(`needs the equipment you said you have`);
  } else if (exercise.minTier === 0) {
    bits.push(`needs no equipment, so it travels`);
  }

  if (exercise.stapleScore === 0) {
    bits.push("and it is a staple rather than a variant");
  }

  return bits.join(", ") + ".";
}

/**
 * Plain-language description of a session (PR-07).
 * Deterministic - same session produces same description.
 *
 * Example: "Push day. Six exercises, 18 working sets, about 60 minutes.
 * Two horizontal presses, one vertical press, and three isolation movements
 * for chest, shoulders, and triceps."
 */
export function describeSession(session, exerciseLibrary) {
  const { focus, exercises, minutes } = session;
  const totalSets = exercises.reduce((sum, ex) => sum + ex.sets, 0);
  const exerciseCount = exercises.length;

  // Count movement patterns
  const patternCounts = {};
  const muscleGroups = new Set();

  exercises.forEach(ex => {
    const exerciseDetails = exerciseLibrary.find(e => e.id === ex.exerciseId);
    if (!exerciseDetails) return;

    const pattern = exerciseDetails.movementPattern;
    patternCounts[pattern] = (patternCounts[pattern] || 0) + 1;
    muscleGroups.add(exerciseDetails.muscleGroup);
  });

  // Build description parts
  const parts = [];

  // Focus name
  const focusName = focus.charAt(0).toUpperCase() + focus.slice(1).replace('-', ' ');
  parts.push(`${focusName} day.`);

  // Exercise and set count
  parts.push(`${exerciseCount} exercise${exerciseCount !== 1 ? 's' : ''}, ${totalSets} working set${totalSets !== 1 ? 's' : ''}, about ${minutes} minutes.`);

  // Pattern breakdown
  const patternDescriptions = [];
  Object.entries(patternCounts).forEach(([pattern, count]) => {
    const readable = pattern.replace('-', ' ');
    const countWord = count === 1 ? 'one' : count === 2 ? 'two' : count === 3 ? 'three' : count;
    const plural = count === 1 ? '' : 's';
    patternDescriptions.push(`${countWord} ${readable}${plural}`);
  });

  if (patternDescriptions.length > 0) {
    const muscles = Array.from(muscleGroups).join(', ');
    parts.push(`${patternDescriptions.join(', ')} for ${muscles}.`);
  }

  return parts.join(' ');
}

// ------------------------------------------------------------ set budget

/**
 * Minutes -> working sets.
 * [Inference] ~3.5 min per working set once warmup, setup and rest are
 * counted. This constant is the main thing to tune against real sessions.
 */
const MINUTES_PER_SET = 3.5;
const WARMUP_MINUTES = 5;

export function setBudget(minutes) {
  return Math.max(4, Math.round((minutes - WARMUP_MINUTES) / MINUTES_PER_SET));
}

// ------------------------------------------------------------- selection

/** Only real working movements are eligible for a generated session. */
const TRAINABLE = new Set(["strength", "powerlifting", "olympic weightlifting"]);

export function eligible(library, tier, focus, excludedIds = []) {
  const groups = new Set(FOCUS_GROUPS[focus]);
  const excluded = new Set(excludedIds);
  return library.filter(
    (e) =>
      e.minTier <= tier &&
      TRAINABLE.has(e.category) &&
      groups.has(e.muscleGroup) &&
      e.images.length > 0 &&
      !excluded.has(e.id)
  );
}

/**
 * Deterministic shuffle so the same seed gives the same week. A user who
 * reopens the app mid-workout must not get a different plan.
 */
function seededPick(candidates, seed) {
  if (candidates.length === 0) return null;
  let h = 2166136261 >>> 0;
  const s = String(seed);
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return candidates[h % candidates.length];
}

/**
 * Build one session.
 * Walks the focus's pattern priority list in a loop, taking one exercise per
 * pattern per pass, until the set budget is spent. Cycling rather than
 * exhausting one pattern is what keeps a push day from being four presses.
 */
export function buildSession(library, { tier, minutes, focus, seed = "", excludedIds = [] }) {
  const pool = eligible(library, tier, focus, excludedIds);
  const patterns = FOCUS_PATTERNS[focus];
  const budget = setBudget(minutes);

  const chosen = [];
  const usedIds = new Set();
  const usedGroups = [];
  let sets = 0;
  let pass = 0;

  while (sets < budget && pass < 12) {
    let progressed = false;

    for (const pattern of patterns) {
      if (sets >= budget) break;

      let candidates = pool.filter(
        (e) => e.movementPattern === pattern && !usedIds.has(e.id)
      );

      // First pass favours compounds, later passes allow isolation. This
      // must NOT apply to the isolation slot itself, or a curl slot gets
      // filled by whatever compound happens to share the biceps group.
      if (pass === 0 && pattern !== "isolation") {
        const compounds = candidates.filter((e) => e.mechanic === "compound");
        if (compounds.length) candidates = compounds;
      }

      // Prefer canonical lifts, BEFORE the spread filter. Ordering matters:
      // spreading first can strand the generator on an obscure variant just
      // because it belongs to a different muscle group.
      const bestScore = Math.min(...candidates.map((e) => e.stapleScore));
      const staples = candidates.filter((e) => e.stapleScore === bestScore);
      if (staples.length) candidates = staples;

      // PR-02: Filter near-duplicates from session.
      // Must run BEFORE stapleScore ranking, or generator keeps selecting
      // canonical lift and then rejecting it. If filtering empties pool,
      // relaxation happens automatically in filterNearDuplicates.
      const diversityResult = filterNearDuplicates(candidates, chosen.map(c =>
        pool.find(e => e.id === c.exerciseId)
      ).filter(Boolean));
      if (diversityResult.pool.length) {
        candidates = diversityResult.pool;
      }
      // Note: diversityResult.relaxed flag could be surfaced to UI here

      // Avoid hammering one muscle group three times in a row, but only when
      // doing so still leaves a real choice.
      // Try to avoid the last two groups. If that leaves nothing, fall back to
      // avoiding just the last one, so a two-group day like arms alternates
      // biceps and triceps instead of stacking three triceps movements.
      let spread = candidates.filter((e) => !usedGroups.slice(-2).includes(e.muscleGroup));
      if (!spread.length) {
        spread = candidates.filter((e) => !usedGroups.slice(-1).includes(e.muscleGroup));
      }
      if (spread.length) candidates = spread;

      const pick = seededPick(candidates, `${seed}|${focus}|${pattern}|${pass}`);
      if (!pick) continue;

      chosen.push({
        exerciseId: pick.id,
        name: pick.name,
        sets: pick.defaultSets,
        reps: pick.defaultReps,
        restSeconds: pick.defaultRestSeconds,
        movementPattern: pick.movementPattern,
        muscleGroup: pick.muscleGroup,
        minTier: pick.minTier,
        substitutes: pick.substitutes,
      });
      usedIds.add(pick.id);
      usedGroups.push(pick.muscleGroup);
      sets += pick.defaultSets;
      progressed = true;
    }

    if (!progressed) break; // pool exhausted, stop rather than spin
    pass++;
  }

  return {
    focus,
    tier,
    minutes,
    targetSets: budget,
    actualSets: sets,
    exercises: chosen,
  };
}

/** Build the whole week. */
export function buildWeek(library, { tier, minutes, daysPerWeek, seed = "wk", excludedIds = [] }) {
  const split = chooseSplit(daysPerWeek, tier);
  return split.map((focus, i) =>
    buildSession(library, { tier, minutes, focus, seed: `${seed}|d${i}`, excludedIds })
  );
}

/** Swap one exercise for the best available alternative at this tier. */
/**
 * Find the best swap for an exercise at the given tier.
 *
 * Uses similarity scoring to avoid returning near-duplicate exercises.
 * Returns the best available swap, or null if none exists.
 *
 * @param {array} library - Full exercise library
 * @param {string} exerciseId - ID of exercise to swap
 * @param {number} tier - Current equipment tier
 * @param {object} opts - Optional: placed (exercises in session), excludedIds, skippedTodayIds
 * @returns {object|null} Best swap exercise or null
 */
export function swap(library, exerciseId, tier, opts = {}) {
  const current = library.find((e) => e.id === exerciseId);
  if (!current) return null;

  const swaps = rankSwapCandidates(current, library, {
    tier,
    placed: opts.placed || [],
    excludedIds: opts.excludedIds || [],
    skippedTodayIds: opts.skippedTodayIds || [],
    limit: 1,
  });

  return swaps.length > 0 ? swaps[0].exercise : null;
}

/**
 * Choose the next focus in a split, skipping what was trained most recently.
 *
 * @param {array} split - The full split array (e.g., ["push", "pull", "legs"])
 * @param {array} recentFocuses - Focus names from recent workouts, most recent first
 * @returns {string} The next focus to train
 */
export function nextSessionFocus(split, recentFocuses = []) {
  if (!split || split.length === 0) {
    throw new Error('split cannot be empty');
  }

  // No history: start at the beginning
  if (recentFocuses.length === 0) {
    return split[0];
  }

  const lastFocus = recentFocuses[0];
  const currentIndex = split.indexOf(lastFocus);

  // Last focus not in current split (user changed days): start at beginning
  if (currentIndex === -1) {
    return split[0];
  }

  // Wrap to the next entry
  const nextIndex = (currentIndex + 1) % split.length;
  return split[nextIndex];
}

/**
 * Progressive overload: determine the next target for an exercise given history.
 *
 * @param {object} exercise - The exercise from the library with defaultReps, weightIncrementLb, weightIncrementKg, progression
 * @param {object} lastSet - The lastSets document: { weight, unit, reps, durationSeconds, sets, missStreak }
 * @param {string} units - User's preferred units ('lb' or 'kg')
 * @returns {object} { weight, unit, targetReps, targetSeconds, deloaded, reason }
 */
export function nextPrescription(exercise, lastSet, units = 'lb') {
  // No history: show prescribed range, no target
  if (!lastSet) {
    return {
      weight: null,
      unit: units,
      targetReps: null,
      targetSeconds: null,
      deloaded: false,
      reason: null,
    };
  }

  const progression = exercise.progression;
  const increment = units === 'kg' ? exercise.weightIncrementKg : exercise.weightIncrementLb;
  const range = exercise.defaultReps; // e.g., "6-10"
  const [rangeBottom, rangeTop] = range.split('-').map(Number);

  // Duration exercises: increment by 5 seconds
  if (progression === 'duration') {
    const lastDuration = lastSet.durationSeconds || 0;
    return {
      weight: null,
      unit: units,
      targetReps: null,
      targetSeconds: lastDuration + 5,
      deloaded: false,
      reason: `You held ${lastDuration}s last time, so aim for ${lastDuration + 5}s.`,
    };
  }

  // Reps-only exercises: increment by 1 rep
  if (progression === 'reps') {
    const lastReps = lastSet.reps || rangeBottom;
    return {
      weight: null,
      unit: units,
      targetReps: lastReps + 1,
      targetSeconds: null,
      deloaded: false,
      reason: `You hit ${lastReps} last time, so aim for ${lastReps + 1}.`,
    };
  }

  // Load-based exercises: double progression
  const lastWeight = lastSet.weight || 0;
  const lastReps = lastSet.reps || rangeBottom;
  const missStreak = lastSet.missStreak || 0;

  // Check for deload: two consecutive misses
  if (missStreak >= 2) {
    const deloadWeight = Math.max(
      increment,
      Math.floor((lastWeight * 0.9) / increment) * increment
    );
    return {
      weight: deloadWeight,
      unit: units,
      targetReps: rangeBottom,
      targetSeconds: null,
      deloaded: true,
      reason: `You missed the bottom of the range twice, so drop to ${deloadWeight} ${units} and rebuild.`,
    };
  }

  // Check if user hit top of range on all sets (need to look at the last session data)
  // Since we only have representative values in lastSet, we check if lastReps >= rangeTop
  const hitTop = lastReps >= rangeTop;

  if (progression === 'reps_first') {
    // Push reps to top + 4 before adding weight
    const threshold = rangeTop + 4;
    if (lastReps >= threshold) {
      // Add weight and reset to bottom
      const newWeight = lastWeight + increment;
      return {
        weight: newWeight,
        unit: units,
        targetReps: rangeBottom,
        targetSeconds: null,
        deloaded: false,
        reason: `You hit ${lastReps} reps last time (top + 4), so this goes up ${increment} ${units} to ${newWeight} ${units}.`,
      };
    } else {
      // Keep weight, add 1 rep
      return {
        weight: lastWeight,
        unit: units,
        targetReps: lastReps + 1,
        targetSeconds: null,
        deloaded: false,
        reason: `You hit ${lastReps} last time, so aim for ${lastReps + 1}. You need ${threshold} to add weight.`,
      };
    }
  }

  // Normal load progression
  if (hitTop) {
    // Add weight and reset to bottom
    const newWeight = lastWeight + increment;
    return {
      weight: newWeight,
      unit: units,
      targetReps: rangeBottom,
      targetSeconds: null,
      deloaded: false,
      reason: `You hit ${rangeTop} reps last time, so this goes up ${increment} ${units} to ${newWeight} ${units}.`,
    };
  } else {
    // Keep weight, add 1 rep
    return {
      weight: lastWeight,
      unit: units,
      targetReps: lastReps + 1,
      targetSeconds: null,
      deloaded: false,
      reason: `You hit ${lastReps} last time, so aim for ${lastReps + 1}.`,
    };
  }
}
